import { parseISO } from "date-fns";
import { z } from "zod";
import { AppointmentStatusSchema } from "./appointmentTypes";

const ActorSchema = z.enum(["owner", "customer", "system", "unknown"]);
const EventTypeSchema = z.enum(["created", "status_changed", "rescheduled"]);
const RevisionSchema = z.number().int().positive().max(Number.MAX_SAFE_INTEGER).nullable();
const ScheduleSchema = z.object({
  startAt: z.date().nullable(), endAt: z.date().nullable(), locationId: z.string().nullable(),
});
const ScheduleDtoSchema = z.object({
  start_at: z.string().nullable(), end_at: z.string().nullable(), location_id: z.string().nullable(),
});
export const AppointmentEventSchema = z.object({
  id: z.string(),
  appointmentId: z.string(),
  eventType: EventTypeSchema,
  fromStatus: AppointmentStatusSchema.nullable(),
  toStatus: AppointmentStatusSchema,
  actorId: z.string().nullable(),
  actorType: ActorSchema,
  previousRevision: RevisionSchema,
  appointmentRevision: RevisionSchema,
  reason: z.string().nullable(),
  oldSchedule: ScheduleSchema.nullable(),
  newSchedule: ScheduleSchema,
  createdAt: z.date(),
});
const AppointmentEventDtoSchema = z.object({
  id: z.string(),
  appointment_id: z.string(),
  event_type: EventTypeSchema,
  from_status: AppointmentStatusSchema.nullable(),
  to_status: AppointmentStatusSchema,
  actor_id: z.string().nullable(),
  actor_type: ActorSchema,
  previous_revision: RevisionSchema,
  appointment_revision: RevisionSchema,
  reason: z.string().nullable(),
  old_schedule: ScheduleDtoSchema.nullable(),
  new_schedule: ScheduleDtoSchema,
  created_at: z.string(),
});
export type AppointmentEventDto = z.infer<typeof AppointmentEventDtoSchema>;
export type AppointmentEvent = z.infer<typeof AppointmentEventSchema>;
export type AppointmentEventSchedule = z.infer<typeof ScheduleSchema>;

function fromScheduleDto(dto: z.infer<typeof ScheduleDtoSchema>): AppointmentEventSchedule {
  return {
    startAt: dto.start_at ? parseISO(dto.start_at) : null,
    endAt: dto.end_at ? parseISO(dto.end_at) : null,
    locationId: dto.location_id,
  };
}

export function fromAppointmentEventDtoToAppointmentEvent(dto: AppointmentEventDto): AppointmentEvent {
  const data = AppointmentEventDtoSchema.parse(dto);
  return {
    id: data.id,
    appointmentId: data.appointment_id,
    eventType: data.event_type,
    fromStatus: data.from_status,
    toStatus: data.to_status,
    actorId: data.actor_id,
    actorType: data.actor_type,
    previousRevision: data.previous_revision,
    appointmentRevision: data.appointment_revision,
    reason: data.reason,
    oldSchedule: data.old_schedule ? fromScheduleDto(data.old_schedule) : null,
    newSchedule: fromScheduleDto(data.new_schedule),
    createdAt: parseISO(data.created_at),
  };
}
